'use client';

import Autoplay from 'embla-carousel-autoplay';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel.component';
import { Gallery } from '@/components/ui/gallery.component';
import { useModal } from '@/hooks/use-modal.hook';
import { DotsVariantEnum } from '@/components/ui/carousel-dot-button.component';
import { cn } from '@/utils/cn-tw-merge';

export interface GalleryImage {
  id: string;
  name: string;
  src: string;
}

interface GalleryCarouselProps {
  images: GalleryImage[];
  className?: string;
  altText: string;
  showDots?: boolean;
  imageWidth?: number;
  imageHeight?: number;
  mobileWidth?: number;
  mobileHeight?: number;
  desktopWidth?: number;
  desktopHeight?: number;
}

const galleryStyles = tv({
  slots: {
    dots: '',
    arrows: 'hidden',
  },

  variants: {
    hasImage: {
      true: {
        dots: 'md:hidden',
        arrows: 'md:flex',
      },
    },
  },
});

export function GalleryCarousel({
  altText,
  images,
  className,
  showDots = true,
  imageWidth = 312,
  imageHeight = 220,
  mobileWidth,
  mobileHeight,
  desktopWidth,
  desktopHeight,
}: GalleryCarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const plugin = useRef(Autoplay({ delay: 4000, stopOnInteraction: true }));
  const [currentIndex, setCurrentIndex] = useState(0);

  const { arrows, dots } = galleryStyles();

  const { open, onOpenChange } = useModal();

  function openGallery(index: number) {
    setCurrentIndex(index);
    onOpenChange(true);
  }

  const finalMobileWidth = mobileWidth ?? Math.min(700, imageWidth);
  const finalMobileHeight = mobileHeight ?? (finalMobileWidth * imageHeight / imageWidth);
  const finalDesktopWidth = desktopWidth ?? 224;
  const finalDesktopHeight = desktopHeight ?? (finalDesktopWidth * imageHeight / imageWidth);

  return (
    <div
      ref={carouselRef}
      className={`w-full md:max-w-[34.5rem] 3xl:max-w-[41.5rem] place-self-center md:px-[2.75rem] ${className}`}
      style={{
        '--mobile-width': `${finalMobileWidth}px`,
        '--mobile-height': `${finalMobileHeight}px`,
        '--desktop-width': `${finalDesktopWidth}px`,
        '--desktop-height': `${finalDesktopHeight}px`,
      } as React.CSSProperties}
    >
      <Carousel
        orientation="horizontal"
        plugins={[plugin.current]}
        onMouseEnter={plugin.current.stop}
        onMouseLeave={plugin.current.reset}
        showArrows
        arrowsClassName={arrows({ hasImage: images.length > 0 })}
        showDots={showDots}
        hideDotsWhenCantScroll
        dotsColorVariant={DotsVariantEnum.GRAY}
        dotsClassName={cn('gap-1', dots())}
        opts={{
          loop: false,
        }}
      >
        <CarouselContent ref={carouselRef} className="gap-4 md:gap-6 mx-1 md:mx-0">
          {images.map((image, index) => (
            <CarouselItem
              key={index}
              className={cn([
                'relative rounded-xxl w-[var(--mobile-width)] h-[var(--mobile-height)] md:w-[var(--desktop-width)] md:h-[var(--desktop-height)]',
                'pl-0',
              ])}
            >
              <button
                className="block w-full h-full"
                onClick={() => openGallery(index)}
              >
                <Image
                  className="rounded-xxl transition-opacity duration-200 ease-in-out hover:opacity-90 cursor-pointer object-cover w-full h-full"
                  src={image.src}
                  alt={altText}
                  width={imageWidth}
                  height={imageHeight}
                  loading="lazy"
                />
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {open && <Gallery open={open} images={images} currentIndex={currentIndex} onOpenChange={onOpenChange} />}
    </div>
  );
}
